<template>
  <v-bottom-sheet v-if="shouldRender" v-model="model" min-height="150px" max-height="80%" scrollable scroll-strategy="block">
    <v-card :color="bgColor" class="rounded-t-xl d-flex flex-column" width="100%" height="100%" :style="{ transform: `translateY(${dragOffset}px)` }">
      <!-- Header: drag-to-close + close button -->
      <div class="d-flex px-4 ga-4 flex-row justify-space-between align-center sheet_header">
        <v-avatar v-if="siteLogo" :image="getResourceUrl(`/cdn/${sportTitle}/site/logo-icon.svg`)" size="36" />
        <span v-else></span>
        
        <!-- drag handle only -->
        <div
        class="drag_handle"
        v-touch="touch"
        @mousedown="onMouseDown"
        role="button"
        aria-label="Drag to close"
        tabindex="0"
        @keydown.space.prevent
        @keydown.enter.prevent
        >
        <span class="bottom_sheet_line"></span>
        <p v-if="title" class="ma-2 text-body-2 font-weight-medium text-pre-wrap" style="position: relative; top: 7px;">
          {{ title }}
        </p>
      </div>
      
      
      <!-- stop click from being eaten by drag listeners -->
      <v-btn class="mx-0" density="comfortable" icon="mdi-close" variant="text" @click.stop="onCloseButton" :ripple="false" />
    </div>
    
    <!-- Body -->
    <div class="px-4 mb-4 sheet_body">
      <component :is="component" />
    </div>
  </v-card>
</v-bottom-sheet>
</template>

<script lang="ts" setup>
import { getResourceUrl, sportTitle, isMobile, updateViewport } from '@/services/admin_actions/apiUtils'
import { computed, type Component, ref, onBeforeUnmount, onMounted, watch } from 'vue'

// Simple theme type
type BgColorVariant = 'primary' | 'grey' | 'white'

// Public props (identity + UI + persistence switches)
const props = withDefaults(defineProps<{
  id: string                    // Unique identifier for this BottomSheet instance (used in localStorage key)
  modelValue: boolean           // v-model: true=open, false=closed
  bgColor: BgColorVariant       // Card color theme (e.g., 'primary' | 'grey' | 'white')
  component: string | Component // The component to render inside the sheet body
  siteLogo?: boolean            // Show the site logo in the header (default: true)
  title?: string                // Optional title text shown next to the drag handle

  // Persistence controls
  persistDismissal?: boolean    // If true, clicking the close button stores a token to keep it hidden (default: true)
  ttlDays?: number              // How many days to keep it hidden after button-close (default: 30)
  disablePersistence?: boolean  // Master off switch: never read/write tokens (ignores the two props above)

  // Desktop visibility
  showOnDesktop: boolean        // Required: if true, sheet can render on desktop; if false, mobile-only
}>(), {
  siteLogo: true,
  persistDismissal: true,
  ttlDays: 30,
  disablePersistence: false,
  title: '',
})

// Events the sheet can broadcast
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'closed'): void                 // Any close path
  (e: 'dismissed'): void              // Token written (close button)
  (e: 'blocked-open'): void           // Tried to open while token active
}>()

// Local two-way binding for v-model
const model = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

// Only render on mobile unless showOnDesktop=true
const shouldRender = computed(() => isMobile.value || !!props.showOnDesktop)

// Keep viewport listener updated (so isMobile works)
onMounted(() => {
  window.addEventListener('resize', updateViewport)
  updateViewport()
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateViewport)
})

// Storage key is namespaced by the sheet id
// Include sport in key so tokens are isolated per site (e.g., squash/tennis)
const storageKey = computed(() => `bottomsheet:${sportTitle.value.toLowerCase()}:${props.id}`)

// Dismissal token model
type DismissToken = { id: string; dismissedAt: string; expiresAt: string }

// Read token from storage
const readDismissal = (): DismissToken | null => {
  try {
    const raw = localStorage.getItem(storageKey.value)
    if (!raw) return null
    const token = JSON.parse(raw) as DismissToken
    if (!token?.id || !token?.expiresAt) return null
    if (token.id !== props.id) return null
    return token
  } catch {
    return null
  }
}

// Is a token past its expiry?
const isExpired = (t: DismissToken) => Date.now() >= Date.parse(t.expiresAt)

// Remove token completely
const clearDismissal = () => localStorage.removeItem(storageKey.value)

// Save a new token (used only on close button)
const writeDismissal = () => {
  if (props.disablePersistence || !props.persistDismissal) return
  const now = new Date()
  const expires = new Date(now)
  expires.setDate(now.getDate() + (props.ttlDays ?? 30))
  const token: DismissToken = {
    id: props.id,
    dismissedAt: now.toISOString(),
    expiresAt: expires.toISOString(),
  }
  localStorage.setItem(storageKey.value, JSON.stringify(token))
}

// Public queries: “is this sheet currently dismissed?” and “when will it reopen?”
const isDismissed = (): boolean => {
  const token = readDismissal()
  return !!token && !isExpired(token)
}
const getExpiry = (): Date | null => {
  const token = readDismissal()
  return token ? new Date(token.expiresAt) : null
}

// Public controls: open/close from parent code
// - open(opts.force) lets you bypass tokens if needed
const open = (opts?: { force?: boolean }) => {
  if (!opts?.force && !props.disablePersistence && props.persistDismissal) {
    const token = readDismissal()
    if (token && !isExpired(token)) {
      emit('blocked-open')
      return
    }
  }
  emit('update:modelValue', true)
}
// - close(opts.persist) decides whether to write a token
const close = (opts?: { persist?: boolean }) => {
  if (opts?.persist) onCloseButton()
  else closeWithoutPersist()
}

// Expose useful methods to parent via ref
defineExpose({
  open,               // programmatic open
  close,              // programmatic close
  clearDismissal,     // clear token
  isDismissed,        // check token state
  getExpiry,          // get token expiry
  storageKey,         // debug helper
})

// On page load: if a valid token exists, keep the sheet closed
onMounted(() => {
  if (props.disablePersistence || !props.persistDismissal) return
  const token = readDismissal()
  if (!token) return
  if (isExpired(token)) {
    clearDismissal()
    return
  }
  emit('update:modelValue', false) // ensure closed on mount
})

// Guard: if something tries to open the sheet while dismissed, block it
watch(
() => props.modelValue,
(v) => {
  if (!v) return
  if (props.disablePersistence || !props.persistDismissal) return
  const token = readDismissal()
  if (token && !isExpired(token)) {
    emit('update:modelValue', false) // revert open
    emit('blocked-open')
  }
}
)

// Close helpers used by different paths
// - onCloseButton: close and remember not to show again until expiry
const onCloseButton = () => {
  writeDismissal()
  emit('dismissed')
  emit('update:modelValue', false)
  emit('closed')
}
// - closeWithoutPersist: close temporarily; allow future opens
const closeWithoutPersist = () => {
  emit('update:modelValue', false)
  emit('closed')
}

/* =========================================================
SECTION B — Drag-to-close (touch + mouse)
Purpose: let users drag the sheet down to close it,
without creating a dismissal token
========================================================= */

// “How far have we dragged?” used to animate the sheet
const THRESHOLD = 180
const dragOffset = ref(0)
const touchStartY = ref(0)
const lastTouchDy = ref(0)

// Touch wrapper used by v-touch
type TouchWrapper = { originalEvent: TouchEvent; offsetY: number }

// Touch handlers (mobile): allow drag to move, and close if dragged far enough
const touch = {
  options: { passive: false }, // we call preventDefault below
  start: (e: TouchWrapper) => {
    // Reset drag and remember where the finger started
    dragOffset.value = 0
    e.originalEvent.preventDefault()
    e.originalEvent.stopPropagation()
    touchStartY.value = e.originalEvent.touches?.[0]?.clientY ?? 0
    lastTouchDy.value = 0
    
    console.groupCollapsed('📱 BottomSheet drag (touch:start)')
    console.log({ startY: touchStartY.value, THRESHOLD })
    console.groupEnd()
  },
  move: (e: TouchWrapper) => {
    // Follow the finger but clamp within the threshold
    e.originalEvent.preventDefault()
    const currentY = e.originalEvent.touches?.[0]?.clientY ?? touchStartY.value
    const dy = currentY - touchStartY.value
    lastTouchDy.value = dy
    dragOffset.value = Math.min(Math.max(dy, 0), THRESHOLD)
    
    console.groupCollapsed('📱 BottomSheet drag (touch:move)')
    console.log({ currentY, dy, dragOffset: dragOffset.value, THRESHOLD })
    console.groupEnd()
  },
  end: (e: TouchWrapper) => {
    // If user dragged far enough: close (no token). Otherwise, snap back.
    e.originalEvent.preventDefault()
    const dy = lastTouchDy.value
    const shouldClose = dy > THRESHOLD
    
    console.groupCollapsed('📱 BottomSheet drag (touch:end)')
    console.log({ dy, shouldClose, THRESHOLD })
    console.groupEnd()
    
    if (shouldClose) { dragOffset.value = 0; closeWithoutPersist() }
    else { dragOffset.value = 0 }
  }
}

// Mouse drag (desktop): same idea as touch, using mouse events
const isDragging = ref(false)
const startY = ref(0)

// Mouse down handler to initiate drag
const onMouseDown = (e: MouseEvent) => {
  isDragging.value = true
  startY.value = e.clientY
  dragOffset.value = 0
  
  console.groupCollapsed('🖱️ BottomSheet drag (mouse:down)')
  console.log({ startY: startY.value, dragOffset: dragOffset.value, THRESHOLD })
  console.groupEnd()
  
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp, { once: true })
}

// Mouse move handler to update drag position
const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value) return
  const dy = e.clientY - startY.value
  dragOffset.value = Math.min(Math.max(dy, 0), THRESHOLD)
  
  console.groupCollapsed('🖱️ BottomSheet drag (mouse:move)')
  console.log({ dy, dragOffset: dragOffset.value, THRESHOLD })
  console.groupEnd()
  
  if (dy > THRESHOLD) { onMouseUp(); closeWithoutPersist() }
}

// Mouse up handler to finalize drag
const onMouseUp = () => {
  console.groupCollapsed('🖱️ BottomSheet drag (mouse:up)')
  console.log({ finalOffset: dragOffset.value, THRESHOLD })
  console.groupEnd()
  
  isDragging.value = false
  dragOffset.value = 0
  window.removeEventListener('mousemove', onMouseMove)
}

// Cleanup when component is destroyed
onBeforeUnmount(() => window.removeEventListener('mousemove', onMouseMove))
</script>

<style lang="scss">
/* ── Layering ─────────────────────────────────────────── */
.v-bottom-sheet {
  z-index: 100002 !important; /* keep above mobile nav */
}

/* ── Sheet container ──────────────────────────────────── */
.v-bottom-sheet__content {
  border-radius: 50px !important;
}

.v-card {
  transition: transform 160ms ease; /* smooth snap-back */
}

/* ── Header & drag handle ─────────────────────────────── */
.sheet_header {
  user-select: none;
  cursor: grab;
}

.sheet_header .drag_handle {
  cursor: grab;
  touch-action: none;   /* allow touchmove to reach JS */
  user-select: none;
  width: 100%;
  min-height: 32px;     /* easier to grab */
}

.sheet_header .drag_handle:active {
  cursor: grabbing;
}

/* Visual drag line (decorative only) */
.bottom_sheet_line {
  display: inline-block;
  pointer-events: none; /* don't steal events */
  width: 35px;
  height: 4px;
  background-color: rgb(var(--v-theme-grey-400));
  border-radius: 50px;
  position: absolute;
  transform: translate(-50%, -50%);
  left: 50%;
  margin-top: 5px;
}

/* ── Body scroll behavior ─────────────────────────────── */
.sheet_body {
  overflow-y: auto;
  max-height: 100%;
  scroll-behavior: smooth;
  
  /* hide scrollbars */
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  &::-webkit-scrollbar { display: none; }
}
</style>